import DashboardLayout from '../../components/layout/DashboardLayout';
import { User, Bell, Shield, Database, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';

export default function Settings() {
  const { user } = useAuth();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportAllData = async () => {
    try {
      setIsExporting(true);

      // Buscar todos os dados do sistema
      const [assetsResult, maintenancesResult, fieldsResult] = await Promise.all([
        supabase.from('assets').select('*').order('created_at', { ascending: false }),
        supabase.from('maintenances').select('*').order('created_at', { ascending: false }),
        supabase.from('dynamic_fields').select('*').order('order_index', { ascending: true })
      ]);

      if (assetsResult.error) throw assetsResult.error;
      if (maintenancesResult.error) throw maintenancesResult.error;
      if (fieldsResult.error) throw fieldsResult.error;

      const assets = assetsResult.data || [];
      const maintenances = maintenancesResult.data || [];
      const fields = fieldsResult.data || [];

      // Criar workbook
      const wb = XLSX.utils.book_new();

      // Sheet 1: Ativos
      if (assets.length > 0) {
        const assetsData = assets.map(asset => {
          const row: any = {
            'Código': asset.code,
            'Status': asset.status,
            'Criado em': new Date(asset.created_at).toLocaleString('pt-BR')
          };

          // Adicionar campos dinâmicos
          if (asset.dynamic_data) {
            Object.entries(asset.dynamic_data).forEach(([key, value]) => {
              row[key] = value;
            });
          }

          return row;
        });

        const ws1 = XLSX.utils.json_to_sheet(assetsData);
        XLSX.utils.book_append_sheet(wb, ws1, 'Ativos');
      }

      // Sheet 2: Manutenções
      if (maintenances.length > 0) {
        const maintenancesData = maintenances.map(maintenance => ({
          'Código do Ativo': maintenance.asset_code,
          'Tipo': maintenance.type === 'preventive' ? 'Preventiva' : 
                  maintenance.type === 'corrective' ? 'Corretiva' : 'Preditiva',
          'Descrição': maintenance.description,
          'Status': maintenance.status === 'scheduled' ? 'Agendada' :
                    maintenance.status === 'in_progress' ? 'Em Andamento' :
                    maintenance.status === 'completed' ? 'Concluída' : 'Cancelada',
          'Data Agendada': maintenance.scheduled_date ? new Date(maintenance.scheduled_date).toLocaleDateString('pt-BR') : '',
          'Data de Conclusão': maintenance.completed_date ? new Date(maintenance.completed_date).toLocaleDateString('pt-BR') : '',
          'Custo': maintenance.cost ? `R$ ${maintenance.cost.toFixed(2)}` : '',
          'Técnico': maintenance.technician || '',
          'Criado em': new Date(maintenance.created_at).toLocaleString('pt-BR')
        }));

        const ws2 = XLSX.utils.json_to_sheet(maintenancesData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Manutenções');
      }

      // Sheet 3: Campos Dinâmicos
      if (fields.length > 0) {
        const fieldsData = fields.map(field => ({
          'Nome do Campo': field.field_name,
          'Tipo': field.field_type,
          'Obrigatório': field.is_required ? 'Sim' : 'Não',
          'Ordem': field.order_index,
          'Criado em': new Date(field.created_at).toLocaleString('pt-BR')
        }));

        const ws3 = XLSX.utils.json_to_sheet(fieldsData);
        XLSX.utils.book_append_sheet(wb, ws3, 'Campos Dinâmicos');
      }

      // Sheet 4: Resumo do Sistema
      const summary = [
        { 'Métrica': 'Total de Ativos', 'Valor': assets.length },
        { 'Métrica': 'Total de Manutenções', 'Valor': maintenances.length },
        { 'Métrica': 'Campos Dinâmicos', 'Valor': fields.length },
        { 'Métrica': 'Data da Exportação', 'Valor': new Date().toLocaleString('pt-BR') },
        { 'Métrica': 'Usuário', 'Valor': user?.email || '' }
      ];

      const ws4 = XLSX.utils.json_to_sheet(summary);
      XLSX.utils.book_append_sheet(wb, ws4, 'Resumo');

      // Gerar arquivo
      const fileName = `backup_completo_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      alert('✅ Exportação concluída com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar dados:', error);
      alert('❌ Erro ao exportar dados. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600 mt-2">Personalize o sistema conforme suas necessidades</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Perfil do Usuário</h3>
                <p className="text-sm text-gray-600">Gerencie suas informações pessoais</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                <input
                  type="text"
                  placeholder="Seu nome"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                />
              </div>
              <button className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Salvar Alterações
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Bell className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Notificações</h3>
                <p className="text-sm text-gray-600">Configure alertas e lembretes</p>
              </div>
            </div>
            <div className="space-y-4">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">Manutenções agendadas</span>
                <input type="checkbox" className="w-5 h-5 text-teal-600 rounded cursor-pointer" defaultChecked />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">Novos ativos cadastrados</span>
                <input type="checkbox" className="w-5 h-5 text-teal-600 rounded cursor-pointer" defaultChecked />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-gray-700">Relatórios semanais</span>
                <input type="checkbox" className="w-5 h-5 text-teal-600 rounded cursor-pointer" />
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Segurança</h3>
                <p className="text-sm text-gray-600">Proteja sua conta</p>
              </div>
            </div>
            <div className="space-y-4">
              <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                Alterar Senha
              </button>
              <button className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                Ativar Autenticação em Duas Etapas
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Dados e Backup</h3>
                <p className="text-sm text-gray-600">Gerencie seus dados</p>
              </div>
            </div>
            <div className="space-y-4">
              <button 
                onClick={handleExportAllData}
                disabled={isExporting}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Exportando...' : 'Exportar Todos os Dados'}
              </button>
              <button className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer">
                Excluir Conta
              </button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
